import { message } from 'antd';
import { MapData_Base, MapData_ModelUtil, MapData_ProcessStatus } from '~/common/typescript/models/MapData';
import { remote_mapData_getOne } from '~/common/typescript/apis/MapDataAPIs';
import { parseMapDataMetadata } from './mapData/MapDataMetadataParser';
import {
  MapDataVectorLayer,
  MAP_DATA_SOURCE_ID_PREFIX,
  MAP_DATA_LAYER_ID_PREFIX,
} from './mapData/MapDataVectorLayer';
import { MapDataRasterLayer } from './mapData/MapDataRasterLayer';

export { MAP_DATA_SOURCE_ID_PREFIX, MAP_DATA_LAYER_ID_PREFIX };

const MapDataUtils = {
  addToMap: async (mapData: MapData_Base, aboveMapDataId?: number) => {
    const map = window.map;
    if (!map) return;

    // 1. Kiểm tra trạng thái xử lý
    if (
      mapData.processStatus === MapData_ProcessStatus.SUBMITED ||
      mapData.processStatus === MapData_ProcessStatus.PROCESSING ||
      mapData.processStatus === MapData_ProcessStatus.PENDING
    ) {
      message.info(
        `Dữ liệu "${mapData.name}" đang được xử lý tạo lát gạch (${MapData_ModelUtil.getTextStatus(
          mapData.processStatus,
        )}). Vui lòng đợi trong giây lát.`,
      );
      return;
    }

    if (mapData.processStatus === MapData_ProcessStatus.FAILED) {
      message.error(
        `Dữ liệu "${mapData.name}" xử lý thất bại: ${mapData.processMessage || 'Lỗi không xác định'}`,
      );
      return;
    }

    let parsed = parseMapDataMetadata(mapData);
    let targetMapData = mapData;

    // 2. Nếu thiếu metadata, thử lấy dữ liệu mới nhất từ server
    if (!parsed.ogrInfo && !parsed.gdalInfo && !parsed.tileFile && targetMapData.id) {
      try {
        console.log('MapDataUtils - Metadata trống trên client, đang fetch dữ liệu mới nhất từ server...');
        const fresh = await remote_mapData_getOne(targetMapData.id);
        if (fresh) {
          targetMapData = fresh;
          parsed = parseMapDataMetadata(fresh);
        }
      } catch (err) {
        console.warn('MapDataUtils - Lỗi khi fetch fresh mapData:', err);
      }
    }

    console.log('MapDataUtils - Layer type:', {
      isVector: parsed.isVector,
      isRaster: parsed.isRaster,
      driverShortName: parsed.metadata.driverShortName,
      hasTileFile: !!parsed.tileFile,
    });

    const layersOfAboveMapData = map.getStyle()?.layers?.filter((layer: any) => {
      return layer.id.includes(MAP_DATA_LAYER_ID_PREFIX + aboveMapDataId);
    });
    const theFirstAboveLayerId = layersOfAboveMapData?.[0]?.id;

    if (parsed.ogrInfo || parsed.isVector || parsed.tileFile) {
      await MapDataVectorLayer.addToMap(targetMapData, parsed, theFirstAboveLayerId);
    } else if (parsed.gdalInfo || parsed.isRaster) {
      await MapDataRasterLayer.addToMap(targetMapData, parsed, theFirstAboveLayerId);
    } else {
      console.warn('MapDataUtils - No ogrInfo or gdalInfo found for mapData:', targetMapData.id, targetMapData.name);
      message.warning(`Không thể hiển thị dữ liệu "${targetMapData.name}": thiếu thông tin metadata (ogrInfo/gdalInfo)`);
    }
  },

  removeFromMap: (mapData: MapData_Base) => {
    const map = window.map;
    if (!map) return;

    const sourceId = MAP_DATA_SOURCE_ID_PREFIX + mapData.id;

    // Xóa các layers trước
    const layers = map.getStyle()?.layers;
    layers?.forEach((layer: any) => {
      if (layer.source === sourceId) {
        map.removeLayer(layer.id);
      }
    });

    // Xóa source sau
    if (map.getSource(sourceId)) {
      map.removeSource(sourceId);
    }

    // Dọn dẹp event listeners
    if (mapData.id) {
      MapDataVectorLayer.cleanupEvents(mapData.id);
    }
  },

  moveToBelowOf: (mapData: MapData_Base, aboveMapDataId: number) => {
    const map = window.map;
    if (!map) return;

    const layersOfAboveMapData = map.getStyle()?.layers?.filter((layer: any) => {
      return layer.id.includes(MAP_DATA_LAYER_ID_PREFIX + aboveMapDataId);
    });
    const theFirstAboveLayerId = layersOfAboveMapData?.[0]?.id;

    const sourceId = MAP_DATA_SOURCE_ID_PREFIX + mapData.id;
    const layers = map.getStyle()?.layers;
    layers?.forEach((layer: any) => {
      if (layer.source === sourceId) {
        map.moveLayer(layer.id, theFirstAboveLayerId);
      }
    });
  },

  moveToTop: (mapData: MapData_Base) => {
    const map = window.map;
    if (!map) return;

    const sourceId = MAP_DATA_SOURCE_ID_PREFIX + mapData.id;
    const layers = map.getStyle()?.layers;
    layers?.forEach((layer: any) => {
      if (layer.source === sourceId) {
        map.moveLayer(layer.id);
      }
    });
  },
};

export default MapDataUtils;
